import { FileUp, Loader2, Search, Trash2, Zap } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader, SortDropdown, type SortOption, useConfirm, VisibilityBadge } from "@/components/shared";
import { useToast } from "@/components/shared/Toast";
import { ImportSkillModal } from "@/components/skills/ImportSkillModal";
import { getApiErrorMessage } from "@/lib";
import { agentService, skillService } from "@/lib/services";
import type { SkillSummary } from "@/types";

type SkillSort = "name-asc" | "name-desc" | "recent";

const SORT_OPTIONS: SortOption<SkillSort>[] = [
  { id: "name-asc", label: "Name (A–Z)" },
  { id: "name-desc", label: "Name (Z–A)" },
  { id: "recent", label: "Recently added" },
];

export function Skills() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState<SkillSort>("name-asc");
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await skillService.list({ pageSize: 100 });
      setSkills(res.data);
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to load skills"), "error");
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (skill: SkillSummary) => {
    // Look up import siblings + attachment count to inform the confirmation.
    let siblingAgents: { uuid: string; name: string }[] = [];
    let attachedCount = 0;
    try {
      const impact = await skillService.deleteImpact(skill.uuid);
      siblingAgents = impact.data.siblingAgents;
      attachedCount = impact.data.attachedAgentCount;
    } catch {
      // Non-fatal — fall back to a plain delete confirmation.
    }

    let message = `Delete "${skill.name}"? This can't be undone.`;
    if (attachedCount > 1) {
      message += ` This skill is attached to ${attachedCount} agents; they will lose it.`;
    }
    const confirmed = await confirm({
      title: "Delete Skill",
      message,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await skillService.delete(skill.uuid);
      setSkills((prev) => prev.filter((s) => s.uuid !== skill.uuid));
      showToast("Skill deleted", "success");
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to delete skill"), "error");
      return;
    }

    // Offer to delete the agent(s) imported alongside this skill.
    if (siblingAgents.length > 0) {
      const names = siblingAgents.map((a) => `"${a.name}"`).join(", ");
      const alsoDelete = await confirm({
        title: "Delete imported agent?",
        message: `${names} ${siblingAgents.length === 1 ? "was" : "were"} imported with this skill. Delete ${siblingAgents.length === 1 ? "it" : "them"} too?`,
        confirmText: "Delete agent",
        variant: "danger",
      });
      if (alsoDelete) {
        await Promise.allSettled(siblingAgents.map((a) => agentService.delete(a.uuid)));
        showToast("Imported agent deleted", "success");
      }
    }
  };

  const query = searchQuery.trim().toLowerCase();
  const filtered = useMemo(() => {
    const matched = query
      ? skills.filter(
          (s) =>
            s.name.toLowerCase().includes(query) ||
            (s.description ?? "").toLowerCase().includes(query) ||
            (s.category ?? "").toLowerCase().includes(query),
        )
      : skills;

    const sorted = [...matched];
    const ts = (s: SkillSummary) => {
      const t = new Date(s.createdAt).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    sorted.sort((a, b) => {
      switch (sort) {
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "recent":
          return ts(b) - ts(a);
        default:
          return a.name.localeCompare(b.name);
      }
    });
    return sorted;
  }, [skills, query, sort]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        icon={Zap}
        title="My Skills"
        subtitle="Reusable instruction bundles you can attach to agents"
        actions={
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient hover:opacity-90 text-white font-medium rounded-lg transition"
          >
            <FileUp className="w-4 h-4" />
            Import Skill
          </button>
        }
      >
        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-quaternary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search skills..."
              className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
          <SortDropdown options={SORT_OPTIONS} value={sort} onChange={setSort} />
        </div>
      </PageHeader>

      <div className="flex-1 overflow-auto p-6">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-border-primary py-16 px-4 text-center">
            <Zap className="w-12 h-12 text-surface-tertiary mb-3" />
            <h3 className="text-content-primary font-medium">
              {query ? "No skills match your search" : "No skills yet"}
            </h3>
            <p className="text-content-tertiary text-sm mt-1">
              {query ? "Try a different search." : "Import a skill bundle to get started."}
            </p>
            {!query && (
              <button
                type="button"
                onClick={() => setShowImport(true)}
                className="mt-4 flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
              >
                <FileUp className="w-4 h-4" />
                Import Skill
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((skill) => (
              <SkillCard key={skill.uuid} skill={skill} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>

      {showImport && (
        <ImportSkillModal
          onClose={() => setShowImport(false)}
          onImported={() => {
            // Refresh the list but keep the modal open so the user can review
            // the result and rename any created agents.
            load();
          }}
        />
      )}
    </div>
  );
}

function SkillCard({ skill, onDelete }: Readonly<{ skill: SkillSummary; onDelete: (s: SkillSummary) => void }>) {
  const toolCount = skill.requiredToolUuids?.length ?? 0;
  return (
    <div className="group bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${skill.color || "#F59E0B"}20` }}
          >
            <Zap className="w-4 h-4" style={{ color: skill.color || "#F59E0B" }} />
          </div>
          <h3 className="text-sm font-medium text-content-primary truncate">{skill.name}</h3>
        </div>
        <div className="flex items-center gap-1">
          <VisibilityBadge visibility={skill.visibility} />
          {!skill.isSystem && (
            <button
              type="button"
              onClick={() => onDelete(skill)}
              className="p-1 rounded text-content-quaternary hover:text-red-400 hover:bg-interactive-active opacity-0 group-hover:opacity-100 transition"
              title="Delete skill"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {skill.description && <p className="text-xs text-content-tertiary mt-2 line-clamp-2">{skill.description}</p>}
      <div className="flex items-center gap-3 mt-3 text-xs text-content-quaternary">
        {skill.category && <span className="truncate">{skill.category}</span>}
        <span>
          {toolCount} tool{toolCount === 1 ? "" : "s"}
        </span>
        {skill.extension && <span className="truncate">via {skill.extension.name}</span>}
      </div>
    </div>
  );
}
